import { Paper, useTheme } from "@mui/material";
import { useEnvContext } from "context/EnvContext";
import {
  APP_PROVIDER_SEARCH,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
} from "core/consts";
import authApi from "core/model/api/endpoints/auth";
import { ENV_PRODUCTION } from "core/model/config";
import { getErrorMessage } from "core/model/utils/errors";
import { isCI } from "core/model/utils/featureFlags";
import { getQueryVariable } from "core/model/utils/urls";
import { AppType, Env, QueryProgress } from "core/types";
import RSButton from "ds_legacy/components/RSButton";
import { TextInputField } from "ds_legacy/components/TextInputField";
import { VerticalLayout } from "ds_legacy/materials/layouts";
import { padding, sizing } from "ds_legacy/materials/metrics";
import { Subheading } from "ds_legacy/materials/typography";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { ReactNode, useEffect, useState } from "react";
import { useTranslations } from "translations";
import Translations from "translations/types";

const getLocalStorageKey = (app: AppType) => `test-env-password-${app}`;

const useLoggedPasswordState = (): [string, (newPassword: string) => void] => {
  const { app } = useEnvContext();
  const localStorageKey = getLocalStorageKey(app);
  const [password, setPassword] = useState(() => {
    return localStorage.getItem(localStorageKey) ?? "";
  });

  useEffect(() => {
    localStorage.setItem(localStorageKey, password);
  }, [password]);

  return [password, setPassword];
};

const getTitle = (app: AppType, translations: Translations): string => {
  switch (app) {
    case APP_PROVIDER_SEARCH:
      return translations.providersearch.preprodLogin;
    default:
      return translations.login.login;
  }
};

const TestEnvLoginForm = ({
  queryProgress,
  setStoredPassword,
}: {
  queryProgress: QueryProgress;
  setStoredPassword: (newPassword: string) => void;
}) => {
  const [password, setPassword] = useState("");
  const translations = useTranslations();
  const theme = useTheme();
  const { app, env, setEnv } = useEnvContext();

  useEffect(() => {
    const envParam = getQueryVariable(window.location.search, "env");
    if (envParam && env !== envParam) setEnv(envParam as Env);
  }, [window.location.search]);

  return (
    <VerticalLayout
      height="100vh"
      style={{
        alignItems: "center",
        backgroundColor: theme.palette.grey[200],
        justifyContent: "center",
      }}
      width="100%"
    >
      <Paper
        elevation={2}
        sx={{
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: padding(2, 3),
          rowGap: sizing(1),
        }}
      >
        <Subheading>{getTitle(app, translations)}</Subheading>
        <TextInputField
          placeholder={translations.newOnboarding.password.caption}
          textInputType="password"
          value={password}
          onChange={setPassword}
        />
        <RSButton
          color="primary"
          id="password-test-env"
          loading={queryProgress}
          variant="contained"
          onClick={() => {
            if (!password) return;
            setStoredPassword(password);
          }}
        >
          {translations.actions.submit}
        </RSButton>
      </Paper>
    </VerticalLayout>
  );
};

const BLOCKED_APPS = [APP_PROVIDER_SEARCH] as AppType[];

const TestEnvLogin = ({ children }: { children: ReactNode }) => {
  const translations = useTranslations();
  const toast = useToast();

  const [isLogged, setIsLogged] = useState(false);
  const [storedPassword, setStoredPassword] = useLoggedPasswordState();
  const [checkingStoredPassword, setCheckingStoredPassword] = useState(true);
  const [queryProgress, setQueryProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );

  useEffect(() => {
    const checkPassword = async () => {
      try {
        if (!storedPassword) {
          return;
        }
        setQueryProgress(QUERY_PROGRESS_PENDING);
        await authApi.testEnvPasswordCheck({
          password: storedPassword,
        });
        setIsLogged(true);
      } catch (err) {
        setIsLogged(false);
        setStoredPassword("");

        console.error("error in test env login:", getErrorMessage(err));
        toast({
          color: "primary",
          message: translations.login.failedAlert.title,
        });
      } finally {
        setCheckingStoredPassword(false);
        setQueryProgress(QUERY_PROGRESS_NOT_STARTED);
      }
    };
    checkPassword();
  }, [storedPassword]);

  if (checkingStoredPassword) {
    return null;
  }

  if (!isLogged) {
    return (
      <TestEnvLoginForm
        queryProgress={queryProgress}
        setStoredPassword={setStoredPassword}
      />
    );
  }

  return <>{children}</>;
};

export const TestEnvLoginProvider = ({ children }: { children: ReactNode }) => {
  const { app, env } = useEnvContext();

  const isDev = window?.location?.origin === "http://localhost:9090";

  if (env === ENV_PRODUCTION || !BLOCKED_APPS.includes(app) || isCI || isDev) {
    return <>{children}</>;
  }

  return <TestEnvLogin>{children}</TestEnvLogin>;
};
